import {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
  useRef,
} from 'react'
import { useLocation } from 'react-router-dom'

interface MenuContextType {
  expandedAccordion: string | undefined
  setExpandedAccordion: (id: string | undefined) => void
  isMenuExpanded: boolean
  setIsMenuExpanded: (expanded: boolean) => void
  isDisabledExpanded: boolean
  setIsDisabledExpanded: (value: boolean) => void
}

const MenuContext = createContext<MenuContextType | undefined>(undefined)

export const useMenuContext = () => {
  const context = useContext(MenuContext)
  if (!context) {
    throw new Error('useMenuContext must be used within MenuProvider')
  }
  return context
}

interface MenuProviderProps {
  children: ReactNode
}

const STORAGE_KEY = 'menu-expanded-accordion'

const getAccordionForPath = (path: string): string | undefined => {
  // OTHER menu - diagnostics, reports, user management
  if (
    path.startsWith('/dashboard/other/') ||
    path.startsWith('/dashboard/diagnostics') ||
    path.startsWith('/dashboard/user-management')
  ) {
    return 'left-menu-other'
  }
  // SENSORS menu - sensors dashboard, zeus, scada, etz
  else if (
    path.startsWith('/dashboard/sensors') ||
    path.startsWith('/dashboard/zeusDashboard') ||
    path.startsWith('/dashboard/scadaDashboard') ||
    path === '/config/sensors/etz'
  ) {
    return 'left-menu-sensors'
  }
  // STATION CONFIG menu - stations, controllers, areas & zones
  else if (path.startsWith('/config/station-config')) {
    return 'left-menu-station-config'
  }
  // DATA CONFIG menu
  else if (path.startsWith('/config/dataConfiguration')) {
    return 'left-menu-data-config'
  }
  // OPERATIONS menu - mapview, programs, widgets, irrigation, simulation, flow history
  else if (
    path.startsWith('/dashboard/mapview') ||
    path.startsWith('/dashboard/programs') ||
    path.startsWith('/dashboard/widgets-dashboard') ||
    path.startsWith('/dashboard/irrigation-overview') ||
    path.startsWith('/dashboard/simulation') ||
    path.startsWith('/dashboard/flowHistory')
  ) {
    return 'left-menu-main-menu'
  }
  // Default: if on any dashboard/operations page, open main menu
  else if (path.startsWith('/dashboard') || path.startsWith('/operations')) {
    return 'left-menu-main-menu'
  }
  // Default: if on any config page, open data config
  else if (path.startsWith('/config')) {
    return 'left-menu-data-config'
  }
  return undefined
}

export const MenuProvider = ({ children }: MenuProviderProps) => {
  const location = useLocation()
  const previousPathname = useRef(location.pathname)

  const [expandedAccordion, setExpandedAccordion] = useState<
    string | undefined
  >(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      if (stored) {
        return stored
      }
    } catch (error) {}
    return getAccordionForPath(location.pathname)
  })

  const [isMenuExpanded, setIsMenuExpanded] = useState(false)
  const [isDisabledExpanded, setIsDisabledExpanded] = useState(false)

  const checkAndSetIsMenuExpanded = (value: boolean) => {
    if (!value && isDisabledExpanded) return
    setIsMenuExpanded(value)
  }
  useEffect(() => {
    try {
      if (expandedAccordion) {
        localStorage.setItem(STORAGE_KEY, expandedAccordion)
      } else {
        localStorage.removeItem(STORAGE_KEY)
      }
    } catch (error) {
      console.error('Error writing to localStorage:', error)
    }
  }, [expandedAccordion])

  useEffect(() => {
    if (previousPathname.current !== location.pathname) {
      previousPathname.current = location.pathname
      const accordionForPath = getAccordionForPath(location.pathname)
      if (accordionForPath) {
        setExpandedAccordion(accordionForPath)
      }
    }
  }, [location.pathname])

  return (
    <MenuContext.Provider
      value={{
        expandedAccordion,
        setExpandedAccordion,
        isMenuExpanded,
        setIsMenuExpanded: checkAndSetIsMenuExpanded,
        isDisabledExpanded,
        setIsDisabledExpanded,
      }}
    >
      {children}
    </MenuContext.Provider>
  )
}
